import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, CreditCard, Copy, Share2, Users, Lock, Trophy, Phone, KeyRound } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import HelplineWidget from '../components/HelplineWidget';

function MobileUpdate() {
  const [mobile, setMobile] = useState('');
  const [saving, setSaving] = useState(false);
  const saveMobile = async () => {
    if (!mobile.trim()) { toast.error('Mobile number enter करें'); return; }
    setSaving(true);
    try {
      await api.put('/profile/mobile', { mobile: mobile.trim() });
      toast.success('Mobile number updated!');
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };
  return (
    <div className="flex items-center gap-2 mt-1">
      <Phone size={12} style={{ color: 'var(--neon-text-muted)' }} />
      <input data-testid="mobile-update-input" type="tel" placeholder="Mobile Number"
        className="text-xs p-1.5 rounded-lg w-32"
        style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
        value={mobile} onChange={e => setMobile(e.target.value)} />
      <button data-testid="mobile-update-btn" onClick={saveMobile} disabled={saving}
        className="text-[10px] px-2 py-1 rounded-lg font-bold"
        style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>
        {saving ? '...' : 'Save'}
      </button>
    </div>
  );
}

function PasswordChange() {
  const [open, setOpen] = useState(false);
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = async () => {
    if (!currentPass) { toast.error('Current password enter करें'); return; }
    if (newPass.length < 6) { toast.error('New password minimum 6 characters'); return; }
    if (newPass !== confirmPass) { toast.error('New password and confirm password match नहीं हो रहे'); return; }
    setLoading(true);
    try {
      const res = await api.put('/profile/change-password', { current_password: currentPass, new_password: newPass });
      toast.success(res.data.message);
      // Update token with new session
      if (res.data.token) localStorage.setItem('neonplay_token', res.data.token);
      setOpen(false); setCurrentPass(''); setNewPass(''); setConfirmPass('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  return (
    <div data-testid="password-change-card" className="glass-card rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold flex items-center gap-2"
          style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
          <KeyRound size={18} /> Change Password
        </h3>
        <button data-testid="toggle-password-change" onClick={() => setOpen(!open)}
          className="text-xs font-bold px-3 py-1.5 rounded-lg"
          style={{ background: open ? 'rgba(255,42,109,0.15)' : 'rgba(5,217,232,0.15)', color: open ? '#FF2A6D' : 'var(--neon-primary)' }}>
          {open ? 'Cancel' : 'Change'}
        </button>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Current Password</Label>
            <Input data-testid="current-password-input" className="neon-input" type="password"
              placeholder="Enter current password" value={currentPass} onChange={e => setCurrentPass(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>New Password</Label>
            <Input data-testid="new-password-input" className="neon-input" type="password"
              placeholder="Min 6 characters" value={newPass} onChange={e => setNewPass(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Confirm New Password</Label>
            <Input data-testid="confirm-password-input" className="neon-input" type="password"
              placeholder="Repeat new password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} />
          </div>
          <button data-testid="save-password-btn" onClick={handleChange} disabled={loading}
            className="w-full btn-neon-primary text-sm disabled:opacity-50">
            {loading ? 'Changing...' : 'Update Password'}
          </button>
          <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>
            Password change करने पर सभी devices से logout हो जाएगा।
          </p>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [referral, setReferral] = useState(null);
  const [payForm, setPayForm] = useState({ upi_id: '', account_number: '', account_ifsc: '', account_holder: '', reason: '' });
  const [isChange, setIsChange] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, rRes] = await Promise.all([api.get('/profile'), api.get('/referral')]);
      setProfile(pRes.data.profile);
      setReferral(rRes.data);
      if (pRes.data.profile.payment_info_set) {
        setPayForm({
          upi_id: pRes.data.profile.upi_id || '',
          account_number: pRes.data.profile.account_number || '',
          account_ifsc: pRes.data.profile.account_ifsc || '',
          account_holder: pRes.data.profile.account_holder || ''
        });
      }
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (profile?.payment_info_set && isChange) {
        if (!payForm.reason.trim()) { toast.error('Reason is required for change request'); setLoading(false); return; }
        await api.post('/profile/change-request', payForm);
        toast.success('Change request sent to admin');
        setIsChange(false);
      } else {
        await api.put('/profile/payment-info', payForm);
        toast.success('Payment info saved!');
        refreshUser();
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const copyReferral = () => {
    if (referral?.referral_code) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const referralLink = `${window.location.origin}/login?ref=${referral.referral_code}`;
      navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied!');
    }
  };

  const shareReferral = () => {
    if (referral?.referral_code) {
      const referralLink = `${window.location.origin}/login?ref=${referral.referral_code}`;
      const message = `Join Mukamari using my link and get bonus!\n${referralLink}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen pb-28 p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Profile Card */}
        <div data-testid="profile-card" className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <UserAvatar name={user?.name} size={64} className="neon-border" />
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
                {user?.name || 'User'}
              </h2>
              <p className="text-sm" style={{ color: 'var(--neon-text-secondary)' }}>{user?.email}</p>
              {user?.mobile && <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{user.mobile}</p>}
              {!user?.mobile && (
                <MobileUpdate />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Balance</p>
              <p className="text-lg font-bold" style={{ color: 'var(--neon-accent)' }}>{user?.wallet_balance || 0}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Bets</p>
              <p className="text-lg font-bold" style={{ color: 'var(--neon-primary)' }}>{user?.total_bets || 0}</p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <PasswordChange />

        {/* Payment Info */}
        <div data-testid="payment-info-card" className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
              <CreditCard size={18} /> Payment Info
            </h3>
            {profile?.payment_info_set && !isChange && (
              <button data-testid="change-payment-btn" onClick={() => setIsChange(true)}
                className="text-xs font-bold px-3 py-1 rounded-lg"
                style={{ color: 'var(--neon-secondary)', border: '1px solid var(--neon-secondary)' }}>
                Request Change
              </button>
            )}
          </div>

          {profile?.payment_info_set && !isChange ? (
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>UPI ID</p>
                <p className="font-medium" style={{ color: 'var(--neon-text)' }}>{profile.upi_id || 'Not set'}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Account Number</p>
                <p className="font-medium" style={{ color: 'var(--neon-text)' }}>{profile.account_number || 'Not set'}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>IFSC Code</p>
                <p className="font-medium" style={{ color: 'var(--neon-text)' }}>{profile.account_ifsc || 'Not set'}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Account Holder</p>
                <p className="font-medium" style={{ color: 'var(--neon-text)' }}>{profile.account_holder || 'Not set'}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePaymentSubmit} className="space-y-3">
              {isChange && (
                <p className="text-xs p-2 rounded-lg mb-2" style={{ background: 'rgba(255,42,109,0.1)', color: 'var(--neon-secondary)' }}>
                  This will send a change request to admin for approval.
                </p>
              )}
              {isChange && (
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-accent)' }}>Reason for Change *</Label>
                  <textarea data-testid="change-reason-input" className="neon-input w-full h-16 p-3 rounded-lg resize-none text-sm" required
                    placeholder="Why do you want to change your payment details?"
                    value={payForm.reason} onChange={(e) => setPayForm(f => ({ ...f, reason: e.target.value }))}
                    style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }} />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>UPI ID</Label>
                <Input data-testid="upi-input" className="neon-input" placeholder="yourname@upi"
                  value={payForm.upi_id} onChange={(e) => setPayForm(f => ({ ...f, upi_id: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Account Number</Label>
                <Input data-testid="account-number-input" className="neon-input" placeholder="Account number"
                  value={payForm.account_number} onChange={(e) => setPayForm(f => ({ ...f, account_number: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>IFSC Code</Label>
                <Input data-testid="ifsc-input" className="neon-input" placeholder="IFSC code"
                  value={payForm.account_ifsc} onChange={(e) => setPayForm(f => ({ ...f, account_ifsc: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Account Holder Name</Label>
                <Input data-testid="account-holder-input" className="neon-input" placeholder="Full name"
                  value={payForm.account_holder} onChange={(e) => setPayForm(f => ({ ...f, account_holder: e.target.value }))} />
              </div>
              <div className="flex gap-3">
                <button type="submit" data-testid="save-payment-btn" disabled={loading}
                  className="flex-1 btn-neon-primary disabled:opacity-50">
                  {loading ? 'Saving...' : isChange ? 'Submit Change Request' : 'Save Payment Info'}
                </button>
                {isChange && (
                  <button type="button" onClick={() => setIsChange(false)}
                    className="px-4 rounded-lg" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-secondary)' }}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Referral Section */}
        <div data-testid="referral-card" className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-bold flex items-center gap-2 mb-4"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
            <Users size={18} /> Referral Program
          </h3>
          <div className="p-4 rounded-lg mb-3" style={{ background: 'var(--neon-elevated)' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Your Referral Code</p>
                <p data-testid="referral-code" className="text-xl font-black"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>
                  {referral?.referral_code || '...'}
                </p>
              </div>
              <div className="flex gap-2">
                <button data-testid="copy-referral-btn" onClick={copyReferral}
                  className="p-2 rounded-lg transition-all hover:scale-110"
                  style={{ background: 'var(--neon-surface)', color: 'var(--neon-primary)' }}>
                  <Copy size={18} />
                </button>
                <button data-testid="share-referral-btn" onClick={shareReferral}
                  className="p-2 rounded-lg transition-all hover:scale-110"
                  style={{ background: 'rgba(37,211,102,0.15)', color: '#25D366' }}>
                  <Share2 size={18} />
                </button>
              </div>
            </div>
            {referral?.referral_code && (
              <p className="text-[10px] break-all mt-1" style={{ color: 'var(--neon-text-muted)' }}>
                {window.location.origin}/login?ref={referral.referral_code}
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-elevated)' }}>
              <Trophy size={16} className="mx-auto mb-1" style={{ color: 'var(--neon-primary)' }} />
              <p className="font-bold text-lg" style={{ color: 'var(--neon-text)' }}>{referral?.referral_count || 0}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Referrals</p>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-elevated)' }}>
              <Lock size={16} className="mx-auto mb-1" style={{ color: 'var(--neon-accent)' }} />
              <p className="font-bold text-lg" style={{ color: 'var(--neon-text)' }}>{referral?.locked_referral_balance || 0}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Locked</p>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-elevated)' }}>
              <p className="font-bold text-lg" style={{ color: 'var(--neon-text)' }}>{referral?.total_bets || 0}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>
                /{referral?.settings?.min_bets_to_unlock || 10} bets
              </p>
            </div>
          </div>
          {referral?.settings && (
            <p className="text-xs mt-3" style={{ color: 'var(--neon-text-muted)' }}>
              Place {referral.settings.min_bets_to_unlock} bets to unlock your referral bonus of {referral.settings.bonus_amount} coins each.
            </p>
          )}
        </div>
      </div>
      <HelplineWidget />
    </div>
  );
}
